import { useEffect, useState, type FormEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { TrainingInstructions } from '@allemnii-pulse/shared';
import { Button } from '../../components/ui/Button.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';
import { InstructionsFields } from './InstructionsFields.js';

export interface GenerateRequest {
  feedback?: string;
  instructions?: TrainingInstructions;
  saveAsProjectDefault: boolean;
}

/**
 * The confirm step before any training generation. The project's instructions apply unless the
 * user opens "Customize this generation", in which case the edited copy is sent for this run
 * only — or, with "Save as project default", also replaces the project's copy.
 */
export function GenerateDialog({
  open,
  onClose,
  title,
  description,
  submitLabel,
  defaults,
  feedback: feedbackMode = 'none',
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  submitLabel: string;
  /** The project's current instructions — the starting point for a customization. */
  defaults: TrainingInstructions;
  /** 'required' for "regenerate with feedback", 'optional' to offer it, 'none' to hide it. */
  feedback?: 'none' | 'optional' | 'required';
  onSubmit: (request: GenerateRequest) => void;
}) {
  const [feedback, setFeedback] = useState('');
  const [customize, setCustomize] = useState(false);
  const [instructions, setInstructions] = useState(defaults);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFeedback('');
    setCustomize(false);
    setInstructions(defaults);
    setSaveAsDefault(false);
    // Reset each time the dialog opens, from whatever the project's instructions are by then —
    // not on every change to `defaults` while it's open, which would wipe the user's edits.
  }, [open]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      feedback: feedback.trim() || undefined,
      ...(customize
        ? { instructions, saveAsProjectDefault: saveAsDefault }
        : { saveAsProjectDefault: false }),
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} className="max-w-2xl">
      {/* On a phone the Dialog itself is the scroller (it's capped to the visible viewport); a
          second one in here would fight it around the on-screen keyboard. */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:max-h-[80vh] md:overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {feedbackMode !== 'none' && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="generate-feedback">
              {feedbackMode === 'required' ? 'What should change?' : 'Feedback (optional)'}
            </Label>
            <Textarea
              id="generate-feedback"
              rows={4}
              maxLength={4000}
              autoFocus
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g. Merge sessions 2 and 3, add a hands-on exercise, shorter bullets…"
            />
          </div>
        )}
        <button
          type="button"
          className="flex items-center gap-1 self-start text-sm font-medium text-accent"
          onClick={() => setCustomize((c) => !c)}
        >
          {customize ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Customize instructions for this generation
        </button>
        {customize && (
          <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
            <InstructionsFields
              idPrefix="generate"
              value={instructions}
              onChange={setInstructions}
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={saveAsDefault}
                onCheckedChange={(c) => setSaveAsDefault(c === true)}
              />
              Save as this project's default
            </label>
          </div>
        )}
        <DialogFooter className="flex-wrap">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            disabled={feedbackMode === 'required' && !feedback.trim()}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
